/**
 * Звук саундборда у клиента.
 *
 * Отдельно от сцены, и это не оговорка: НАЖАТИЕ НЕ МЕНЯЕТ СЦЕНУ.
 * Звук ничего не рисует, его не двигают мышью и не выключают
 * галочкой — он случается один раз и заканчивается сам (docs/04).
 * Класть его в состояние значило бы завести поле, которое надо
 * не забыть сбросить, и однажды его не сбросят.
 *
 * Разбор общий для оверлея и панели: первый играет, вторая гасит
 * кнопку на время кулдауна. Две реализации разошлись бы на первой
 * правке.
 */
/** Что сервер сказал о запущенном звуке. */
import { decodeMessage } from './message.js';
/**
 * Разбирает триггер `sound.play`.
 *
 * Возвращает null для всего остального: вызывающий по этому
 * понимает, что сообщение не про звук, и передаёт дальше.
 *
 * ЗНАЧЕНИЯ ПРИЖИМАЮТСЯ ЗДЕСЬ, как и у громкости канала: они
 * приходят из сети и попадают в узел Web Audio, где NaN
 * и бесконечность дают не тишину, а треск в наушниках у зрителей.
 */
export function parseSoundPlay(raw) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    const message = parsed;
    if (message.t !== 'trigger' || message.kind !== 'sound.play')
        return null;
    if (typeof message.payload !== 'object' || message.payload === null)
        return null;
    const payload = message.payload;
    if (typeof payload.itemId !== 'string' || payload.itemId === '')
        return null;
    return {
        triggerId: typeof message.triggerId === 'string' ? message.triggerId : '',
        itemId: payload.itemId,
        url: typeof payload.url === 'string' ? payload.url : '',
        gain: level(payload.gain),
        loudness: finite(payload.loudness),
        durationMs: positive(payload.durationMs),
    };
}
/**
 * Множитель громкости, выравнивающий звук по цели.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ. Файлы саундборда приходят откуда попало:
 * один записан в тишине студии, другой вырезан из ролика с рекламой.
 * Без выравнивания первый не слышно, а второй рвёт колонки
 * зрителям — и стример узнаёт об этом от них, в комментариях.
 *
 * ВЫРАВНИВАЕТ КЛИЕНТ, А НЕ ПЕРЕКОДИРОВЩИК (4.1): перекодировать
 * с исправленной громкостью значит потерять качество ещё раз
 * и лишиться возможности передумать. Измеренное значение лежит
 * в базе, поправку применяет звуковой движок.
 */
export function loudnessGain(loudness, target = TARGET_LUFS) {
    // Не измеряли — не трогаем. Догадка о громкости хуже, чем её
    // отсутствие: ошибётся она в ту же сторону, что и слух
    if (loudness === null)
        return 1;
    const linear = 10 ** ((target - loudness) / 20);
    // ПРЕДЕЛ УСИЛЕНИЯ ЕСТЬ, и он не из осторожности. Тихая запись
    // с шумом, поднятая в двадцать раз, — это шум на полную громкость,
    // а не спасённый звук. Ослабление ограничено с другой стороны
    // по той же причине: звук, опущенный в сто раз, всё равно
    // что выключен, и человек будет жать кнопку снова
    return Math.min(MAX_BOOST, Math.max(MIN_CUT, linear));
}
/** Целевая громкость, LUFS. Та же, что в перекодировщике (4.1). */
export const TARGET_LUFS = -16;
const MAX_BOOST = 4;
const MIN_CUT = 0.1;
function level(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return 1;
    return Math.min(1, Math.max(0, value));
}
function finite(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    return value;
}
function positive(value) {
    const parsed = finite(value);
    if (parsed === null || parsed <= 0)
        return null;
    return parsed;
}
/**
 * Разбирает список играющих звуков.
 *
 * Приходит при подключении и только панелям: оверлей звук не
 * подхватывает с середины — событие, начатое заново, выдало бы
 * зрителю хвост того, что он уже слышал.
 */
export function parseSoundPlaying(raw) {
    const message = messageOf(raw, 'soundPlaying');
    if (message === null)
        return null;
    const sounds = message.sounds;
    if (!Array.isArray(sounds))
        return null;
    const out = [];
    for (const each of sounds) {
        const entry = soundPlayingOf(each);
        if (entry !== null)
            out.push(entry);
    }
    return out;
}
function soundPlayingOf(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const entry = raw;
    if (typeof entry.triggerId !== 'string' || entry.triggerId === '')
        return null;
    if (typeof entry.itemId !== 'string' || entry.itemId === '')
        return null;
    const started = finite(entry.startedAtMs);
    if (started === null || started <= 0)
        return null;
    return {
        triggerId: entry.triggerId,
        itemId: entry.itemId,
        startedAtMs: started,
        durationMs: positive(entry.durationMs),
    };
}
/**
 * Разбирает просьбу замолчать.
 *
 * Приходит и тому, кто её отправил: панель держит тот же список,
 * и «остановлено» она должна узнать оттуда же, откуда узнала
 * «запущено».
 */
export function parseSoundStop(raw) {
    const message = messageOf(raw, 'soundStop');
    if (message === null)
        return null;
    const triggerId = message.triggerId;
    if (typeof triggerId !== 'string' || triggerId === '')
        return null;
    return { triggerId };
}
/**
 * Собирает просьбу остановить звук.
 *
 * Без нажатия — остановить всё. Это НЕ стоп-кран: тот глушит весь
 * звук эфира, включая музыку и ролики, и снимается отдельным
 * действием. Здесь кончаются только нажатые кнопки.
 */
export function soundStopMessage(triggerId) {
    return { t: 'soundStop', triggerId: triggerId ?? '' };
}
/** Разбирает конверт сообщения нужного вида. */
function messageOf(raw, type) {
    const parsed = decodeMessage(raw);
    if (parsed === null)
        return null;
    if (parsed.t !== type)
        return null;
    return parsed;
}
//# sourceMappingURL=sound.js.map